import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Terminal,
  Hexagon,
  Cpu,
  Network,
  Activity,
  Shield,
  Globe,
  Zap,
} from "lucide-react";
import AnimatedCounter from "@/components/AnimatedCounter";
import GlitchText from "@/components/GlitchText";
import RadarWidget from "@/components/RadarWidget";
import DataBars from "@/components/DataBars";
import DynamicCoordinates from "@/components/DynamicCoordinates";

function LoadingBar({ className = "h-8 w-32", textClass = "text-[10px]" }) {
  return (
    <div className={`bg-[#05060F]/60 border border-[#1E2BFF]/40 relative overflow-hidden rounded-sm flex items-center justify-center ${className}`}>
      {/* Sweeping gradient highlight */}
      <motion.div
        className="absolute top-0 bottom-0 w-full bg-gradient-to-r from-transparent via-[#00F0FF]/20 to-transparent"
        initial={{ left: "-100%" }}
        animate={{ left: "100%" }}
        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
      />

      {/* Loading text with animated dots */}
      <div className={`relative z-10 font-mono tracking-[0.25em] text-[#00F0FF] uppercase flex items-center ${textClass}`}>
        Loading
        <span className="flex w-4 justify-between ml-1">
          <motion.span animate={{ opacity: [0, 1, 0] }} transition={{ duration: 1.5, repeat: Infinity, times: [0, 0.5, 1] }}>.</motion.span>
          <motion.span animate={{ opacity: [0, 1, 0] }} transition={{ duration: 1.5, repeat: Infinity, times: [0, 0.5, 1], delay: 0.2 }}>.</motion.span>
          <motion.span animate={{ opacity: [0, 1, 0] }} transition={{ duration: 1.5, repeat: Infinity, times: [0, 0.5, 1], delay: 0.4 }}>.</motion.span>
        </span>
      </div>
    </div>
  );
}

import heroImg from "@/assets/hero-bg.png";
import aiImg from "@/assets/agentic_ai.png";
import iotImg from "@/assets/iot.png";
import dtImg from "@/assets/digital_transformation.png";
import appImg from "@/assets/app_dev-hero-bg.png";
import engageCtaBg from "@/assets/engage_cta_bg.png";

const HERO_BG = heroImg;
const AI_IMG = aiImg;
const IOT_IMG = iotImg;
const DT_IMG = dtImg;
const APP_IMG = appImg;
const services = [
  {
    id: "agentic-ai",
    tag: "SYS.01",
    title: "Agentic AI",
    desc: "Developing open, safe, and transparent autonomous systems. We focus on ethical AI frameworks that prioritize long-term utility over short-term hype.",
    icon: Cpu,
    img: AI_IMG,
    span: "lg:col-span-7 lg:row-span-2",
  },
  {
    id: "iot",
    tag: "SYS.02",
    title: "Internet of Things",
    desc: "Architecting resilient telemetry for the public and private sectors. Our edge-to-cloud designs focus on interoperability and sustainable infrastructure.",
    icon: Network,
    img: IOT_IMG,
    span: "lg:col-span-5",
  },
  {
    id: "app-dev",
    tag: "SYS.03",
    title: "Application Development",
    desc: "Championing the \"Craft of Code.\" We build API-first systems that serve as blueprints for modern, observable, and secure engineering.",
    icon: Terminal,
    img: APP_IMG,
    span: "lg:col-span-5",
  },
  {
    id: "transformation",
    tag: "SYS.04",
    title: "Digital Transformation",
    desc: "Empowering organizations through shared knowledge. We help teams move beyond legacy tech by teaching the principles of data-native operations.",
    icon: Hexagon,
    img: DT_IMG,
    span: "lg:col-span-12",
  },
];

const stats = [
  { value: "120+", label: "Shipped Systems" },
  { value: "2.4B", label: "Daily Events Processed" },
  { value: "38", label: "Enterprise Operators" },
  { value: "99.99%", label: "Platform Uptime" },
];

const TECH = [
  "REACT 19", "FASTAPI", "KUBERNETES", "POSTGRES", "MONGODB", "KAFKA",
  "LANGGRAPH", "TENSORRT", "MQTT", "AWS", "GCP", "AZURE", "TERRAFORM",
  "RUST", "GO", "TYPESCRIPT", "PYTHON",
];

const capabilities = [
  { icon: Globe, label: "Global-scale deployments" },
  { icon: Zap, label: "Build for Excellence" },
  { icon: Activity, label: "Shared Technical Literacy" },
  { icon: Shield, label: "Infrastructure for Good" },
];

// Hero headline words (for staggered reveal)
const HERO_LINE_1 = ["ENGINEERING"];
const HERO_LINE_2 = ["POST-CLOUD"];

const wordFade = {
  hidden: { opacity: 0, y: 60, filter: "blur(8px)" },
  show: (i) => ({
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.2 + i * 0.08 },
  }),
};

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* ================= HERO ================= */}
      <section className="relative min-h-[100vh] overflow-hidden">
        {/* Background image + overlay */}
        <div
          className="absolute inset-0 -z-10 hero-img"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 -z-10 hero-overlay" />

        {/* Animated data-wave SVG layer */}
        <svg
          className="absolute inset-0 -z-10 w-full h-full data-wave pointer-events-none"
          viewBox="0 0 1400 800"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path d="M0,420 Q350,320 700,400 T1400,380" />
          <path d="M0,500 Q350,600 700,480 T1400,520" />
          <path d="M0,600 Q350,500 700,580 T1400,560" />
          <path d="M0,320 Q350,420 700,300 T1400,340" />
        </svg>

        {/* HUD top row */}
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-4 relative">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center justify-between font-mono text-[10px] tracking-[0.3em] uppercase text-[#9BA1D6]/80"
          >
            <div className="flex items-center gap-3">
              <span className="pulse-dot" />
              <span>LAB.STATUS // ACTIVE</span>
            </div>
            <div className="hidden md:block"><DynamicCoordinates prefix="LAT " /></div>
            <div className="hidden md:block">v.2.5.1 / BUILD 2025.12</div>
          </motion.div>
        </div>

        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <div className="grid grid-cols-12 gap-6 items-start pt-14 pb-28">
            {/* LEFT — Headline + CTAs */}
            <div className="col-span-12 lg:col-span-8">
              <motion.div
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: "auto" }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="flex items-center gap-4 mb-6"
              >
                <span className="h-px w-14 bg-[rgb(var(--accent))] shadow-[0_0_12px_rgb(var(--accent)/0.8)]" />
                <span className="overline">
                  RESEARCH LABS / PAN INDIA — GLOBAL
                  <span className="caret" />
                </span>
              </motion.div>

              {/* LINE 1 */}
              <h1 className="font-hero text-4xl sm:text-7xl lg:text-[104px] leading-[0.88] font-black uppercase tracking-[-0.03em] text-white">
                <motion.span
                  className="block"
                  initial="hidden"
                  animate="show"
                >
                  {HERO_LINE_1.map((w, i) => (
                    <motion.span
                      key={w}
                      custom={i}
                      variants={wordFade}
                      className="inline-block mr-3"
                    >
                      {w}
                    </motion.span>
                  ))}
                </motion.span>

                {/* LINE 2 */}
                <motion.span
                  className="block mt-1"
                  initial="hidden"
                  animate="show"
                >
                  {HERO_LINE_2.map((w, i) => (
                    <motion.span
                      key={w}
                      custom={i + 1}
                      variants={wordFade}
                      className="inline-block mr-3"
                    >
                      {w}
                    </motion.span>
                  ))}
                </motion.span>

                {/* GLITCH ACCENT */}
                <motion.span
                  className="block mt-2"
                  initial={{ opacity: 0, y: 40, filter: "blur(10px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.7 }}
                >
                  <GlitchText text="ENTERPRISE." />
                </motion.span>
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.95 }}
                className="mt-10 text-[#9BA1D6] text-lg lg:text-xl max-w-2xl leading-relaxed"
              >
                Avinya Research Labs is a collaborative system where academia and industry practitioners unite to solve the most pressing challenges. We don't just ship products; we advance the standard of what technology can achieve.
              </motion.p>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 1.1 }}
                className="mt-10 flex flex-wrap items-center gap-4"
              >
                <Link to="/contact" className="btn-primary" data-testid="hero-cta-contact">
                  Start a Project <ArrowRight size={16} />
                </Link>
                <Link to="/services" className="btn-ghost" data-testid="hero-cta-services">
                  Explore Capabilities
                </Link>
              </motion.div>
            </div>

            {/* RIGHT — Radar + HUD readout */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9, delay: 0.4 }}
              className="col-span-12 lg:col-span-4 hidden lg:flex flex-col gap-5 lg:pl-6"
            >
              <div className="hud-card corner-brackets p-6">
                <div className="flex items-center justify-between mb-5">
                  <div className="overline">// RADAR.TELEMETRY</div>
                  <div className="pulse-dot" />
                </div>
                <RadarWidget />
              </div>

              <div className="hud-card corner-brackets p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="overline">// CORE.READOUT</div>
                  <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-[#9BA1D6]">
                    LIVE
                  </span>
                </div>
                <div className="font-mono text-[11px] text-[#9BA1D6] space-y-2">
                  <Row label="AGENTS" value={<LoadingBar className="h-4 w-[90px]" textClass="text-[8px] leading-none pt-px" />} />
                  <Row label="DEVICES" value={<LoadingBar className="h-4 w-[90px]" textClass="text-[8px] leading-none pt-px" />} />
                  <Row label="EVENTS/S" value={<LoadingBar className="h-4 w-[90px]" textClass="text-[8px] leading-none pt-px" />} />
                  <Row label="UPTIME" value={<LoadingBar className="h-4 w-[90px]" textClass="text-[8px] leading-none pt-px" />} accent />
                </div>
                {/* <DataBars className="!h-12 pt-1" /> */}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 1.4 }}
          className="absolute bottom-24 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 z-10 hidden md:flex"
        >
          <div className="scroll-indicator" />
          <span className="font-mono text-[9px] tracking-[0.35em] uppercase text-[#9BA1D6]">
            SCROLL
          </span>
        </motion.div>

        {/* Bottom marquee */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-b border-[#1E2BFF]/20 bg-[#05060F]/70 backdrop-blur-sm overflow-hidden">
          <div className="marquee py-4 font-mono text-[11px] tracking-[0.35em] uppercase text-[#9BA1D6]/70">
            {[...TECH, ...TECH].map((t, i) => (
              <span key={i} className="px-8 whitespace-nowrap">
                <span className="text-[#00F0FF] mr-3">■</span>
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ================= STATS ================= */}
      {/* <section className="relative border-b border-[#1E2BFF]/20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className={`py-12 px-2 relative scan-sweep ${i !== 0 ? "lg:border-l border-[#1E2BFF]/20" : ""
                } ${i < 2 ? "border-b lg:border-b-0" : ""} ${i % 2 !== 0 ? "border-l lg:border-l" : ""
                }`}
            >
              <div className="font-tech text-4xl lg:text-7xl font-bold tracking-tight text-white flex items-center h-12 lg:h-20">
                <LoadingBar className="h-8 lg:h-12 w-32 lg:w-48" />
              </div>
              <div className="mt-3 flex items-center gap-2">
                <span className="h-px w-4 bg-[#00F0FF]" />
                <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#9BA1D6]">
                  {s.label}
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </section> */}


      {/* ================= SERVICES BENTO ================= */}
      <section className="relative py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
            <div>
              <div className="overline mb-4 flex items-center gap-3">
                <span className="title-line" /> CAPABILITIES.INDEX
              </div>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="font-display text-4xl lg:text-7xl font-bold tracking-tighter text-white leading-[1]"
              >
                Four operating <br />
                <span className="text-[#00F0FF] glow-cyan">systems.</span> One lab.
              </motion.h2>
            </div>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="max-w-md text-[#9BA1D6] text-base leading-relaxed"
            >
              Our practice serves as a bridge between theoretical breakthroughs and industrial application. We operate as a shared resource for global operators.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 auto-rows-auto lg:auto-rows-[280px]">
            {services.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className={`hud-card corner-brackets tilt-card scan-sweep relative overflow-hidden col-span-12 ${s.span}`}
                data-testid={`service-card-${s.id}`}
              >
                <img
                  src={s.img}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover opacity-40 mix-blend-luminosity card-bg-img transition-transform duration-700 hover:scale-105"
                />
                <div className="absolute inset-0 card-image-overlay" />
                <div className="relative z-10 h-full p-7 lg:p-9 flex flex-col justify-between">
                  <div className="flex items-start justify-between">
                    <div className="overline">// {s.tag}</div>
                    <s.icon size={26} className="text-[#00F0FF]" strokeWidth={1.2} />
                  </div>
                  <div>
                    <h3 className="font-display text-2xl lg:text-4xl font-bold text-white tracking-tight">
                      {s.title}
                    </h3>
                    <p className="mt-3 text-[#9BA1D6] text-sm lg:text-base max-w-lg leading-relaxed">
                      {s.desc}
                    </p>
                    <Link
                      to="/services"
                      className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-white link-underline"
                      data-testid={`service-card-${s.id}-link`}
                    >
                      Read Brief <ArrowUpRight size={14} />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= MANIFESTO ================= */}
      <section className="relative py-24 lg:py-32 border-t border-[#1E2BFF]/20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <div className="overline mb-4 flex items-center gap-3">
              <span className="title-line" /> DOCTRINE.01
            </div>
            <h2 className="font-display text-3xl lg:text-6xl font-bold text-white tracking-tighter leading-[1.02]">
              We don't build <br />
              software. We build <br />
              <span className="text-[#00F0FF] glow-cyan">intelligence.</span>
            </h2>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-7 lg:pl-10 lg:border-l border-[#1E2BFF]/25"
          >
            <p className="text-[#C9CDE9] text-lg leading-relaxed">
              Avinya operates at the intersection of a professional studio and a learning institution. Every project is a collaborative effort to solve a "broken" industry standard. Our practitioners and academic partners work side-by-side to ensure that every line of code contributes to a larger body of knowledge. We measure success by the resilience and openness of the systems we leave behind.
            </p>
            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              {capabilities.map(({ icon: Icon, label }, i) => (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="hud-card scan-sweep p-5 flex items-center gap-4"
                >
                  <Icon size={20} className="text-[#00F0FF]" strokeWidth={1.4} />
                  <span className="font-mono text-[12px] tracking-[0.18em] uppercase text-white">
                    {label}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ================= CTA ================= */}
      <section className="relative py-24 overflow-hidden border-t border-[#1E2BFF]/20">
        <img
          src={engageCtaBg}
          alt=""
          className="absolute inset-0 w-full h-full object-cover cta-bg-image"
        />
        <div className="absolute inset-0 cta-bg-gradient pointer-events-none" />
        
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative hud-card corner-brackets shimmer-border p-10 lg:p-16 overflow-hidden"
          >
            <div
              className="absolute -right-40 -top-40 w-full max-w-[500px] aspect-square rounded-full blur-3xl"
              style={{
                background:
                  "radial-gradient(circle, rgb(var(--primary) / 0.45), transparent 60%)",
              }}
            />
            <div className="relative z-10 flex flex-col lg:flex-row items-start lg:items-end justify-between gap-10">
              <div>
                <div className="overline mb-4">// ENGAGE.PROTOCOL</div>
                <h3 className="font-display text-3xl lg:text-6xl font-bold text-white tracking-tighter leading-[1.02]">
                  Brief us on the <br />
                  <span className="text-[#00F0FF] glow-cyan">impossible problem.</span>
                </h3>
              </div>
              <Link to="/contact" className="btn-primary" data-testid="home-cta-bottom">
                Initiate Contact <ArrowRight size={16} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

function Row({ label, value, accent = false }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-dashed border-[#1E2BFF]/25">
      <span className="tracking-[0.2em] uppercase">{label}</span>
      <span className={accent ? "text-[#00F0FF]" : "text-white"}>{value}</span>
    </div>
  );
}
